import { useState, useEffect } from 'react'
import {
  AppBar, Toolbar, Box, Typography, Select, MenuItem,
  IconButton, Tooltip,
} from '@mui/material'
import { AccountCircle, Schedule } from '@mui/icons-material'
import { usePlantContext } from '../../hooks/usePlantContext'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'

// Used only if GET /plants fails. SAP codes, matching models.ReferencePlants()
// (release 1 is Faribault only).
const FALLBACK_PLANTS = [
  { plantCode: '1002', name: 'Faribault, MN Plant', city: 'Faribault, MN' },
]

export default function TopBar() {
  const { plant, switchPlant, planContext, setPlanContext } = usePlantContext()
  const { user, logout } = useAuth()

  const [plants, setPlants] = useState(FALLBACK_PLANTS)
  const [plans, setPlans] = useState([])
  const [stalestAge, setStalestAge] = useState(null)
  const [stalestState, setStalestState] = useState('fresh')

  useEffect(() => {
    api.get('/plants').then(data => {
      if (Array.isArray(data) && data.length > 0) setPlants(data)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    api.get(`/plans?plant=${plant.plantCode}`).then(data => {
      if (Array.isArray(data)) setPlans(data)
      else setPlans([])
    }).catch(() => setPlans([]))

    api.get(`/data/freshness?plant=${plant.plantCode}`).then(data => {
      if (!data || typeof data !== 'object') return
      let worstAge = null
      let worstState = 'fresh'
      const order = { fresh: 0, aging: 1, stale: 2 }
      for (const val of Object.values(data)) {
        if (val && order[val.state] > (order[worstState] || 0)) {
          worstState = val.state
          worstAge = val.lastUpdated
        }
      }
      setStalestState(worstState)
      if (worstAge) setStalestAge(formatRelativeTime(worstAge))
    }).catch(() => {})
  }, [plant.plantCode])

  const freshnessColor = stalestState === 'stale' ? 'error.main' : stalestState === 'aging' ? 'warning.main' : 'success.main'

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.default',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: 10,
      }}
    >
      <Toolbar sx={{ minHeight: 56, gap: 2 }}>
        <Select
          value={plant.plantCode}
          onChange={(e) => {
            const p = plants.find(pl => pl.plantCode === e.target.value)
            if (p) switchPlant(p)
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {plants.map(p => (
            <MenuItem key={p.plantCode} value={p.plantCode}>
              {p.name} — {p.city}
            </MenuItem>
          ))}
        </Select>

        {plans.length > 0 && (
          <Select
            value={planContext?.id || ''}
            onChange={(e) => {
              const selected = plans.find(p => p.id === e.target.value)
              setPlanContext(selected || null)
            }}
            size="small"
            displayEmpty
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">No plan selected</MenuItem>
            {plans.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        )}

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Data freshness — click to go to Data Hub">
          <Box
            component="a"
            href="/data"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: freshnessColor, cursor: 'pointer' }}
          >
            <Schedule sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: freshnessColor, fontWeight: 500 }}>
              {stalestAge || '—'}
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title={user?.displayName || user?.email || 'User'}>
          <IconButton onClick={logout} size="small">
            <AccountCircle />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  )
}
